// eslint-disable-next-line
import React, { useState, useMemo, useRef, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Chart from "react-apexcharts";

const LiveAuctionDashboard = ({
  liveBids = [],
  liveBidRanking = [],
  itemLeaderboard = [],
  onlineSuppliers = [],
  invitedSuppliers = [],
  auctionStatus = "active",
  closingDateTime = "",
  auctionExtendedData = null,
  vendorMasking = false,
  userRole = "buyer",
  itemPrices = {},
  itemReservePrice = {},
  isAuctionClosed = false,
}) => {
  const isBuyer = userRole === "buyer";

  // ── Masking helper ──────────────────────────────────────────────────────
  const maskedNameMap = useMemo(() => {
    if (!vendorMasking || !isBuyer) return {};
    const map = {};
    liveBidRanking.forEach((row, idx) => {
      if (row.supplierCode) map[row.supplierCode] = `Supplier ${idx + 1}`;
    });
    let counter = liveBidRanking.length + 1;
    liveBids.forEach((bid) => {
      if (bid.supplierCode && !map[bid.supplierCode]) {
        map[bid.supplierCode] = `Supplier ${counter++}`;
      }
    });
    return map;
  }, [vendorMasking, isBuyer, liveBidRanking, liveBids]);

  const getMaskedName = (code, name) => {
    if (vendorMasking && isBuyer && auctionStatus !== "ended") {
      return maskedNameMap[code] || "Supplier ?";
    }
    return name || code || "--";
  };

  // ── Stats computation ───────────────────────────────────────────────────
  const stats = useMemo(() => {
    const lowestBid = liveBidRanking.length > 0 ? liveBidRanking[0] : null;
    const lowestBidAmount = lowestBid ? lowestBid.totalBid : 0;
    const lowestBidSupplier = lowestBid
      ? getMaskedName(lowestBid.supplierCode, lowestBid.supplierName)
      : "--";

    const totalBids = liveBids.length;

    const activeSuppliers = liveBidRanking.length;
    const totalInvited = invitedSuppliers.length;

    const extensions = auctionExtendedData?.extensionCount || 0;
    const autoExtensionMins = auctionExtendedData?.extensionMinutes || 2;

    const formattedEnd = closingDateTime
      ? new Date(closingDateTime).toLocaleString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
        })
      : "--";

    // ── Savings calculation (item-wise + overall) ─────────────────────────
    const itemSavingsArr = [];
    let totalSp = 0;
    let totalRp = 0;
    let totalL1 = 0;
    let hasAnySavings = false;

    itemLeaderboard.forEach((item) => {
      const itemId = item.local_id || item.itemCode || item.itemKey;
      let sp = parseFloat(itemPrices[itemId]) || 0;
      const rp = parseFloat(itemReservePrice[itemId]) || 0;
      const l1 = item.rankings?.length
        ? Math.min(...item.rankings.map((r) => parseFloat(r.bidAmount) || Infinity))
        : null;

      // If no start price configured, use first bid (initial quote) as start price
      if (!sp && item.rankings?.length > 0) {
        const highestBid = Math.max(...item.rankings.map((r) => parseFloat(r.bidAmount) || 0));
        if (highestBid > 0) sp = highestBid;
      }

      const spSaving = (sp && l1 != null && l1 > 0) ? sp - l1 : null;
      const rpSaving = (rp && l1 != null && l1 > 0) ? rp - l1 : null;
      const spPct = (spSaving != null && sp) ? (spSaving / sp) * 100 : null;
      const rpPct = (rpSaving != null && rp) ? (rpSaving / rp) * 100 : null;

      if (sp || rp) hasAnySavings = true;

      totalSp += sp;
      totalRp += rp;
      if (l1 != null && l1 > 0 && l1 < Infinity) totalL1 += l1;

      itemSavingsArr.push({
        itemCode: item.itemCode || `Item ${itemSavingsArr.length + 1}`,
        itemDesc: item.itemDesc || "",
        startPrice: sp || null,
        reservePrice: rp || null,
        l1Amount: (l1 != null && l1 < Infinity) ? l1 : null,
        spSaving,
        rpSaving,
        spPct,
        rpPct,
      });
    });

    const totalSpSaving = (totalSp && totalL1) ? totalSp - totalL1 : null;
    const totalRpSaving = (totalRp && totalL1) ? totalRp - totalL1 : null;
    const totalSpPct = (totalSpSaving != null && totalSp) ? (totalSpSaving / totalSp) * 100 : null;
    const totalRpPct = (totalRpSaving != null && totalRp) ? (totalRpSaving / totalRp) * 100 : null;

    return {
      lowestBidAmount,
      lowestBidSupplier,
      activeSuppliers,
      totalInvited,
      totalBids,
      extensions,
      autoExtensionMins,
      formattedEnd,
      itemSavingsArr,
      totalSp,
      totalRp,
      totalSpSaving,
      totalRpSaving,
      totalSpPct,
      totalRpPct,
      hasAnySavings,
    };
  }, [liveBidRanking, liveBids, onlineSuppliers, invitedSuppliers, closingDateTime, auctionExtendedData, itemLeaderboard, itemPrices, itemReservePrice]); // eslint-disable-line

  // ── Helpers ─────────────────────────────────────────────────────────────
  const formatAmount = (amount) => {
    if (amount == null || amount === "") return "--";
    return Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--";
    try {
      return new Date(isoString).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
    } catch { return isoString; }
  };

  const getRankLabel = (rank) => {
    if (rank === 1) return { label: "🥇 L1", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" };
    if (rank === 2) return { label: "🥈 L2", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    if (rank === 3) return { label: "🥉 L3", color: "#7c2d12", bg: "#fff3ed", border: "#fb923c" };
    return { label: `🏅 L${rank}`, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  };

  // ── Leaderboard gap calculation ─────────────────────────────────────────
  const leaderboardWithGap = useMemo(() => {
    if (liveBidRanking.length === 0) return [];
    const lowest = liveBidRanking[0].totalBid;
    return liveBidRanking.map((row, idx) => ({
      ...row,
      rank: idx + 1,
      gap: idx === 0 ? 0 : row.totalBid - lowest,
    }));
  }, [liveBidRanking]);

  // ── Shared section item filter (Leaderboard / Bid Feed / Bid Trend) ────
  const sectionItems = useMemo(() => {
    const list = [{ key: "__all__", label: "All Items" }];
    itemLeaderboard.forEach((item) => {
      list.push({
        key: item.local_id || item.itemCode || item.itemKey || "",
        label: item.itemCode || `Item`,
      });
    });
    return list;
  }, [itemLeaderboard]);

  const [sectionItemFilter, setSectionItemFilter] = useState("__all__");

  // ── Get supplier codes for selected item (used to filter bids) ──────────
  const itemSupplierCodes = useMemo(() => {
    if (sectionItemFilter === "__all__") return null;
    const item = itemLeaderboard.find(
      (it) => (it.local_id || it.itemCode || it.itemKey) === sectionItemFilter
    );
    if (!item || !item.rankings?.length) return new Set();
    return new Set(item.rankings.map((r) => String(r.supplierCode)));
  }, [sectionItemFilter, itemLeaderboard]);

  // ── Helper: get per-item bid amount from bid object ──────────────────────
  const getBidAmountForItem = useCallback((bid, filterKey) => {
    if (filterKey === "__all__") return parseFloat(bid.totalBid) || 0;
    // Check bid.items[] for per-item amount
    if (Array.isArray(bid.items)) {
      const match = bid.items.find(
        (it) => String(it.local_id || it.itemCode || it.itemKey || "") === String(filterKey)
      );
      if (match) return parseFloat(match.bidAmount) || 0;
    }
    // Fallback: check top-level itemKey/itemCode
    const bidKey = bid.itemCode || bid.itemKey || bid.local_id || "";
    if (String(bidKey) === String(filterKey)) return parseFloat(bid.totalBid) || 0;
    return 0;
  }, []);

  // ── Bid Trend Chart ─────────────────────────────────────────────────────
  const bidTrendOptions = useMemo(() => {
    const bidsToChart = !itemSupplierCodes
      ? liveBids
      : liveBids.filter((b) => itemSupplierCodes.has(String(b.supplierCode)));

    const seriesMap = {};
    bidsToChart.forEach((bid) => {
      if (!bid || typeof bid !== "object") return;
      const name = getMaskedName(bid.supplierCode, bid.supplierName);
      const xVal = new Date(bid.bidTime).getTime();
      const yVal = getBidAmountForItem(bid, sectionItemFilter);
      if (!name || isNaN(xVal) || isNaN(yVal) || yVal <= 0) return;
      if (!seriesMap[name]) seriesMap[name] = [];
      seriesMap[name].push({ x: xVal, y: yVal });
    });

    // When an item is selected, add start price as first point for each supplier
    if (sectionItemFilter !== "__all__") {
      const item = itemLeaderboard.find(
        (it) => (it.local_id || it.itemCode || it.itemKey) === sectionItemFilter
      );
      if (item) {
        const startPrice = parseFloat(itemPrices[sectionItemFilter]) || parseFloat(itemPrices[item.local_id]) || parseFloat(itemPrices[item.itemCode]) || 0;
        const firstBidTime = bidsToChart.length > 0
          ? Math.min(...bidsToChart.map((b) => new Date(b.bidTime).getTime()).filter((t) => !isNaN(t)))
          : Date.now();

        if (startPrice > 0) {
          Object.keys(seriesMap).forEach((name) => {
            seriesMap[name].unshift({ x: firstBidTime - 1000, y: startPrice });
          });
          // If no series exist yet for this item, create one
          if (Object.keys(seriesMap).length === 0 && item.rankings?.length > 0) {
            const firstName = getMaskedName(item.rankings[0].supplierCode, item.rankings[0].supplierName);
            seriesMap[firstName] = [{ x: firstBidTime - 1000, y: startPrice }];
          }
        }
      }
    }

    const colors = ["#0f766e", "#2563eb", "#d97706", "#dc2626", "#7c3aed", "#059669", "#ea580c"];
    const seriesNames = Object.keys(seriesMap);

    return {
      options: {
        chart: {
          type: "line",
          height: 280,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit",
        },
        colors: colors.slice(0, seriesNames.length),
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          type: "datetime",
          labels: { style: { fontSize: "10px", colors: "#94a3b8" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px", colors: "#94a3b8" },
            formatter: (val) => (val != null && !isNaN(val)) ? `₹${Number(val).toLocaleString("en-IN")}` : "",
          },
        },
        legend: {
          position: "bottom",
          fontSize: "11px",
          markers: { width: 10, height: 10, radius: 2 },
          itemMargin: { horizontal: 10 },
        },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: {
          theme: "light",
          x: { format: "HH:mm:ss" },
        },
        markers: { size: 3, hover: { size: 5 } },
      },
      series: seriesNames.map((name) => ({
        name,
        data: seriesMap[name].sort((a, b) => a.x - b.x),
      })),
    };
  }, [liveBids, itemSupplierCodes, sectionItemFilter, getBidAmountForItem, itemLeaderboard, itemPrices]); // eslint-disable-line

  // ── Item-wise rankings ──────────────────────────────────────────────────
  const [selectedItemIdx, setSelectedItemIdx] = useState(0);

  // ── Filtered leaderboard ────────────────────────────────────────────────
  const filteredLeaderboard = useMemo(() => {
    if (sectionItemFilter === "__all__") return leaderboardWithGap;
    const item = itemLeaderboard.find(
      (it) => (it.local_id || it.itemCode || it.itemKey) === sectionItemFilter
    );
    if (!item || !item.rankings?.length) return [];
    return item.rankings.map((row, idx) => {
      const lowest = item.rankings[0].bidAmount;
      return {
        ...row,
        rank: idx + 1,
        gap: idx === 0 ? 0 : (row.bidAmount || 0) - (lowest || 0),
        totalBid: row.bidAmount || 0,
      };
    });
  }, [leaderboardWithGap, sectionItemFilter, itemLeaderboard]);

  // ── Filtered bids ───────────────────────────────────────────────────────
  const filteredRecentBids = useMemo(() => {
    const all = liveBids.slice(0, 30);
    if (!itemSupplierCodes) return all.slice(0, 10);
    return all
      .filter((b) => {
        if (!itemSupplierCodes.has(String(b.supplierCode))) return false;
        // Also ensure this bid has a per-item amount for the selected item
        return getBidAmountForItem(b, sectionItemFilter) > 0;
      })
      .slice(0, 10);
  }, [liveBids, itemSupplierCodes, sectionItemFilter, getBidAmountForItem]);

  // ── Stat card modal state (buyer-side) ───────────────────────────────────
  const [showItemModal, setShowItemModal] = useState(false);
  const [modalCard, setModalCard] = useState(null);

  const openModal = useCallback((cardType) => {
    setModalCard(cardType);
    setShowItemModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowItemModal(false);
    setModalCard(null);
  }, []);

  // ── Live feed recent bids 
  // eslint-disable-next-line
  const recentBids = useMemo(() => liveBids.slice(0, 10), [liveBids]);

  // ── Online set 
  const onlineSet = useMemo(
    () => new Set(onlineSuppliers.map((s) => s.supplierCode)),
    [onlineSuppliers]
  );

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* 
          TOP STATS CARDS
       */}
      <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
        {/* Lowest Bid */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("lowestBid")}
          >
            <div style={{ ...statIconWrap, background: "#ecfdf5", color: "#059669" }}>
              <i className="bi bi-arrow-down-circle-fill" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Lowest Bid (INR)</div>
            <div style={{ ...statValueStyle, color: "#d1d5db" }}>--</div>
          </div>
        </Col>

        {/* Savings Achieved */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("savings")}
          >
            <div style={{ ...statIconWrap, background: "#dbeafe", color: "#2563eb" }}>
              <i className="bi bi-graph-up-arrow" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Savings Achieved</div>
            <div style={{ ...statValueStyle, color: "#d1d5db" }}>--</div>
          </div>
        </Col>

        {/* Active Suppliers */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("activeSuppliers")}
          >
            <div style={{ ...statIconWrap, background: "#f3e8ff", color: "#7c3aed" }}>
              <i className="bi bi-people-fill" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Active Suppliers</div>
            <div style={{ ...statValueStyle, color: "#d1d5db" }}>--</div>
          </div>
        </Col>

        {/* Total Bids */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("totalBids")}
          >
            <div style={{ ...statIconWrap, background: "#fef3c7", color: "#d97706" }}>
              <i className="bi bi-lightning-charge-fill" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Total Bids</div>
            <div style={{ ...statValueStyle, color: "#d1d5db" }}>--</div>
          </div>
        </Col>

        {/* Extensions */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("extensions")}
          >
            <div style={{ ...statIconWrap, background: "#fce7f3", color: "#db2777" }}>
              <i className="bi bi-clock-fill" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Extensions</div>
            <div style={{ ...statValueStyle, color: "#db2777" }}>{stats.extensions}</div>
          </div>
        </Col>

        {/* Auction End Time */}
        <Col xs={6} md={4} lg={2}>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => openModal("endTime")}
          >
            <div style={{ ...statIconWrap, background: "#ecfdf5", color: "#0f766e" }}>
              <i className="bi bi-calendar-event-fill" style={{ fontSize: "20px" }}></i>
            </div>
            <div style={statLabelStyle}>Auction End Time</div>
            <div style={{ ...statValueStyle, color: "#0f766e", fontSize: "12px" }}>{stats.formattedEnd}</div>
          </div>
        </Col>
      </Row>

      {/* 
          MIDDLE ROW — Leaderboard | Live Bid Feed | Bid Trend
       */}
      <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
        {/* ── Leaderboard ── */}
        <Col xs={12} md={4}>
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="bi bi-trophy-fill" style={{ color: "#d97706", fontSize: "16px" }}></i>
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>Leaderboard</span>
              </div>
              <select
                value={sectionItemFilter}
                onChange={(e) => setSectionItemFilter(e.target.value)}
                style={{
                  fontSize: "11px", padding: "4px 8px", borderRadius: "4px",
                  border: "1px solid #e2e8f0", cursor: "pointer", fontWeight: 600,
                  background: "#f8fafc", color: "#1e293b", maxWidth: "150px",
                }}
              >
                {sectionItems.map((it) => (
                  <option key={it.key} value={it.key}>{it.label}</option>
                ))}
              </select>
            </div>
            <div style={{ overflowX: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={thStyle}>RANK</th>
                    <th style={thStyle}>SUPPLIER</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{sectionItemFilter === "__all__" ? "TOTAL BID (INR)" : "BID AMOUNT (INR)"}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>GAP</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaderboard.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>
                        Waiting for suppliers to bid...
                      </td>
                    </tr>
                  ) : (
                    filteredLeaderboard.map((row, idx) => {
                      const rm = getRankLabel(row.rank);
                      const isOnline = onlineSet.has(row.supplierCode);
                      return (
                        <tr key={idx} style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: idx === 0 ? "#f0fdf4" : idx % 2 === 0 ? "#fff" : "#fafafa",
                          borderLeft: idx === 0 ? "3px solid #10b981" : "3px solid transparent",
                        }}>
                          <td style={tdStyle}>
                            <span style={{
                              background: rm.bg, color: rm.color,
                              border: `1px solid ${rm.border}`, borderRadius: "6px",
                              padding: "2px 8px", fontWeight: 700, fontSize: "11px",
                              display: "inline-block",
                            }}>{rm.label}</span>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600, color: "#1e293b" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{
                                width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0,
                                background: isOnline ? "#10b981" : "#d1d5db",
                              }} />
                              {getMaskedName(row.supplierCode, row.supplierName)}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: idx === 0 ? "#059669" : "#374151" }}>
                            {formatAmount(row.totalBid)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            {row.gap > 0 ? (
                              <span style={{ color: "#dc2626", fontWeight: 600 }}>+{formatAmount(row.gap)}</span>
                            ) : (
                              <span style={{ color: "#94a3b8" }}>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>

        {/*  Live Bid Feed  */}
        <Col xs={12} md={4}>
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="bi bi-broadcast" style={{ color: "#059669", fontSize: "16px" }}></i>
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>Live Bid Feed</span>
              </div>
              <select
                value={sectionItemFilter}
                onChange={(e) => setSectionItemFilter(e.target.value)}
                style={{
                  fontSize: "11px", padding: "4px 8px", borderRadius: "4px",
                  border: "1px solid #e2e8f0", cursor: "pointer", fontWeight: 600,
                  background: "#f8fafc", color: "#1e293b", maxWidth: "150px",
                }}
              >
                {sectionItems.map((it) => (
                  <option key={it.key} value={it.key}>{it.label}</option>
                ))}
              </select>
            </div>
            <div style={{ maxHeight: "340px", overflowY: "auto", flex: 1 }}>
              {filteredRecentBids.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>
                  No bids received yet.
                </div>
              ) : (
                filteredRecentBids.map((bid, idx) => {
                  const displayName = getMaskedName(bid.supplierCode, bid.supplierName);
                  const rm = getRankLabel(bid.rank || idx + 1);
                  return (
                    <div key={idx} style={{
                      padding: "10px 14px", borderBottom: "1px solid #f1f5f9",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: idx === 0 ? "#f0fdf4" : "#fff",
                      borderLeft: idx === 0 ? "3px solid #10b981" : "3px solid transparent",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{
                          fontSize: "11px", color: "#64748b", fontWeight: 500, minWidth: "58px",
                        }}>{formatTime(bid.bidTime)}</span>
                        <span style={{
                          background: rm.bg, color: rm.color, border: `1px solid ${rm.border}`,
                          borderRadius: "4px", padding: "1px 6px", fontWeight: 700, fontSize: "10px",
                        }}>{rm.label.split(" ")[1]}</span>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>
                          {displayName}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>placed a new bid</span>
                        <span style={{
                          fontWeight: 700, fontSize: "12px", color: idx === 0 ? "#059669" : "#374151",
                          marginLeft: "4px",
                        }}>₹ {formatAmount(getBidAmountForItem(bid, sectionItemFilter) || bid.totalBid)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Col>

        {/*  Bid Trend Chart  */}
        <Col xs={12} md={4}>
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="bi bi-bar-chart-line-fill" style={{ color: "#2563eb", fontSize: "16px" }}></i>
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>Bid Trend</span>
              </div>
              <select
                value={sectionItemFilter}
                onChange={(e) => setSectionItemFilter(e.target.value)}
                style={{
                  fontSize: "11px", padding: "4px 8px", borderRadius: "4px",
                  border: "1px solid #e2e8f0", cursor: "pointer", fontWeight: 600,
                  background: "#f8fafc", color: "#1e293b", maxWidth: "150px",
                }}
              >
                {sectionItems.map((it) => (
                  <option key={it.key} value={it.key}>{it.label}</option>
                ))}
              </select>
            </div>
            <div style={{ padding: "8px 4px", flex: 1, display: "flex", flexDirection: "column" }}>
              {bidTrendOptions.series.length === 0 ? (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "12px" }}>
                  No bid data available for chart.
                </div>
              ) : (
                <Chart
                  options={bidTrendOptions.options}
                  series={JSON.parse(JSON.stringify(bidTrendOptions.series))}
                  type="line"
                  height={280}
                />
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* 
          ITEM-WISE LIVE RANKINGS
       */}
      <div style={sectionCardStyle}>
        <div style={{ ...sectionHeaderStyle, flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-list-check" style={{ color: "#0f766e", fontSize: "16px" }}></i>
            <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>Item-wise Live Rankings</span>
            {itemLeaderboard.length > 0 && (
              <span style={{
                fontSize: "11px", padding: "2px 8px", borderRadius: "10px",
                background: "#ccfbf1", color: "#0f7669", fontWeight: 700,
              }}>{itemLeaderboard.length} Items</span>
            )}
          </div>
          <button style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: "6px",
            background: "#fff", color: "#374151", fontSize: "12px", fontWeight: 600,
            cursor: "pointer",
          }}>
            <i className="bi bi-download" style={{ fontSize: "12px" }}></i> Export
          </button>
        </div>

        {itemLeaderboard.length === 0 ? (
          <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>
            No item rankings available yet.
          </div>
        ) : (
          <Row style={{ margin: 0 }}>
            {/*  Item selector (left)  */}
            <Col xs={12} md={3} style={{ padding: "12px", borderRight: "1px solid #e2e8f0" }}>
              {itemLeaderboard.map((item, idx) => {
                const isActive = selectedItemIdx === idx;
                const bidderCount = item.rankings?.length || 0;
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedItemIdx(idx)}
                    style={{
                      padding: "10px 12px", borderRadius: "6px", marginBottom: "6px",
                      cursor: "pointer", transition: "all 0.15s",
                      background: isActive ? "#f0fdf4" : "#fff",
                      border: isActive ? "1.5px solid #0f766e" : "1.5px solid transparent",
                      borderLeft: isActive ? "3px solid #0f766e" : "3px solid transparent",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "13px", color: isActive ? "#0f766e" : "#1e293b" }}>
                      {item.itemCode || `Item ${idx + 1}`}
                    </div>
                    {item.itemDesc && (
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.itemDesc}
                      </div>
                    )}
                    <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "3px" }}>
                      {bidderCount} Bidders
                    </div>
                  </div>
                );
              })}
            </Col>

            {/*  Rankings table (right)  */}
            <Col xs={12} md={9} style={{ padding: 0 }}>
              {itemLeaderboard[selectedItemIdx] && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                        <th style={thStyle}>RANK</th>
                        <th style={thStyle}>SUPPLIER</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>BID AMOUNT (INR)</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>% SAVINGS</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>LAST BID AT</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(itemLeaderboard[selectedItemIdx].rankings || []).map((row, idx) => {
                        const rm = getRankLabel(row.rank);
                        const savings = row.savingsPercentage != null ? row.savingsPercentage : null;
                        return (
                          <tr key={idx} style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: idx === 0 ? "#f0fdf4" : "#fff",
                            borderLeft: idx === 0 ? "3px solid #10b981" : "3px solid transparent",
                          }}>
                            <td style={tdStyle}>
                              <span style={{
                                background: rm.bg, color: rm.color,
                                border: `1px solid ${rm.border}`, borderRadius: "6px",
                                padding: "2px 8px", fontWeight: 700, fontSize: "11px",
                                display: "inline-block",
                              }}>{rm.label}</span>
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 600, color: "#1e293b" }}>
                              {getMaskedName(row.supplierCode, row.supplierName)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: idx === 0 ? "#059669" : "#374151" }}>
                              {typeof row.bidAmount === "number"
                                ? row.bidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                                : row.bidAmount}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                              {savings != null ? (
                                <span style={{ color: "#059669", fontWeight: 600 }}>{savings.toFixed(1)}%</span>
                              ) : (
                                <span style={{ color: "#94a3b8" }}>-</span>
                              )}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", color: "#64748b", fontSize: "11px" }}>
                              {formatTime(row.bidTime)}
                            </td>
                            <td style={tdStyle}>
                              <MiniSparkline />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Col>
          </Row>
        )}
      </div>

      {/* ── Animations ── */}

      {/* 
          STAT CARD ITEM DETAIL MODAL — click any card to view item-wise details
       */}
      {showItemModal && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "600px",
              maxHeight: "85vh", overflow: "auto", position: "relative",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: "absolute", top: "12px", right: "12px",
                background: "#f1f5f9", border: "none", borderRadius: "50%",
                width: "32px", height: "32px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px", color: "#64748b", zIndex: 1,
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            {/* Header */}
            <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="bi bi-list-check" style={{ color: "#0f766e", fontSize: "20px" }}></i>
                <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                  {modalCard === "lowestBid" && "Item-wise Lowest Bids"}
                  {modalCard === "savings" && "Item-wise Savings"}
                  {modalCard === "activeSuppliers" && "Participating Suppliers"}
                  {modalCard === "totalBids" && "Item-wise Bid Count"}
                  {modalCard === "extensions" && "Extension Details"}
                  {modalCard === "endTime" && "Auction End Time"}
                </span>
              </div>
            </div>

            {/* Item list */}
            <div style={{ padding: "16px 20px" }}>
              {modalCard === "lowestBid" && (
                itemLeaderboard.length === 0
                  ? <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>No item data available.</div>
                  : itemLeaderboard.map((item, idx) => {
                      const lowest = item.rankings?.[0];
                      return (
                        <div key={idx} style={{
                          padding: "10px 14px", borderBottom: "1px solid #f1f5f9",
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          background: idx % 2 === 0 ? "#fff" : "#fafafa",
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: "12px", color: "#1e293b" }}>
                              {item.itemCode || `Item ${idx + 1}`}
                            </div>
                            {item.itemDesc && (
                              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{item.itemDesc}</div>
                            )}
                            {lowest && (
                              <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                                by {getMaskedName(lowest.supplierCode, lowest.supplierName)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: "13px", color: "#059669" }}>
                            {lowest?.bidAmount != null ? `₹ ${formatAmount(lowest.bidAmount)}` : "--"}
                          </div>
                        </div>
                      );
                    })
              )}

              {modalCard === "savings" && (
                stats.itemSavingsArr.length === 0
                  ? <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>No item data available.</div>
                  : <div>
                      {stats.itemSavingsArr.map((row, idx) => {
                        const spColor = (row.spSaving != null && row.spSaving > 0) ? "#059669" : (row.spSaving != null ? "#dc2626" : "#9ca3af");
                        const rpColor = (row.rpSaving != null && row.rpSaving > 0) ? "#059669" : (row.rpSaving != null ? "#dc2626" : "#9ca3af");
                        if (!row.startPrice && !row.reservePrice) return null;
                        return (
                          <div key={idx} style={{
                            marginBottom: "12px", border: "1px solid #e2e8f0",
                            borderRadius: "8px", overflow: "hidden",
                          }}>
                            <div style={{
                              background: "#f9fafb", padding: "10px 14px",
                              display: "flex", alignItems: "center", gap: "8px",
                              borderBottom: "1px solid #e2e8f0",
                            }}>
                              <span style={{ fontSize: "14px" }}>{"\uD83D\uDCE6"}</span>
                              <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                                {row.itemCode}
                              </span>
                              {row.itemDesc && (
                                <span style={{ fontWeight: 400, color: "#64748b", fontSize: "12px" }}>
                                  – {row.itemDesc}
                                </span>
                              )}
                            </div>
                            <div style={{
                              display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
                              gap: "1px", background: "#e2e8f0",
                            }}>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Start Price</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>{row.startPrice ? formatAmount(row.startPrice) : "—"}</div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Reserve Price</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>{row.reservePrice ? formatAmount(row.reservePrice) : "—"}</div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>SP Savings</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: spColor }}>
                                  {row.spSaving != null ? formatAmount(row.spSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: spColor, marginTop: "3px" }}>
                                  {row.spPct != null ? `${row.spPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>RP Savings</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: rpColor }}>
                                  {row.rpSaving != null ? formatAmount(row.rpSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: rpColor, marginTop: "3px" }}>
                                  {row.rpPct != null ? `${row.rpPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Overall Totals */}
                      {stats.hasAnySavings && (
                        <div style={{
                          marginTop: "16px", border: "1px solid #d1d5db",
                          borderRadius: "8px", overflow: "hidden",
                        }}>
                          <div style={{
                            background: "#f3f4f6", padding: "10px 14px", borderBottom: "1px solid #e5e7eb",
                          }}>
                            <span style={{ fontWeight: 700, fontSize: "13px", color: "#374151" }}>
                              {"\uD83D\uDCCA"} Overall Totals
                            </span>
                          </div>
                          <div style={{
                            display: "grid", gridTemplateColumns: (stats.totalSp > 0 && stats.totalRp > 0) ? "1fr 1fr 1fr 1fr" : "1fr 1fr",
                            gap: "1px", background: "#e5e7eb",
                          }}>
                            {stats.totalSp > 0 && (
                              <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total Start Price</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>{formatAmount(stats.totalSp)}</div>
                              </div>
                            )}
                            {stats.totalRp > 0 && (
                              <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total Reserve Price</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>{formatAmount(stats.totalRp)}</div>
                              </div>
                            )}
                            {stats.totalSp > 0 && (
                              <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total SP Savings</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: (stats.totalSpSaving != null && stats.totalSpSaving > 0) ? "#059669" : "#dc2626" }}>
                                  {stats.totalSpSaving != null ? formatAmount(stats.totalSpSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: (stats.totalSpPct != null && stats.totalSpPct > 0) ? "#059669" : "#dc2626", marginTop: "2px" }}>
                                  {stats.totalSpPct != null ? `${stats.totalSpPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                            )}
                            {stats.totalRp > 0 && (
                              <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total RP Savings</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: (stats.totalRpSaving != null && stats.totalRpSaving > 0) ? "#059669" : "#dc2626" }}>
                                  {stats.totalRpSaving != null ? formatAmount(stats.totalRpSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: (stats.totalRpPct != null && stats.totalRpPct > 0) ? "#059669" : "#dc2626", marginTop: "2px" }}>
                                  {stats.totalRpPct != null ? `${stats.totalRpPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
              )}

              {modalCard === "activeSuppliers" && (
                liveBidRanking.length === 0
                  ? <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>No suppliers participating yet.</div>
                  : liveBidRanking.map((row, idx) => {
                      const isOnline = onlineSet.has(row.supplierCode);
                      return (
                        <div key={idx} style={{
                          padding: "10px 14px", borderBottom: "1px solid #f1f5f9",
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          background: idx % 2 === 0 ? "#fff" : "#fafafa",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                              background: isOnline ? "#10b981" : "#d1d5db",
                            }} />
                            <span style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
                              {getMaskedName(row.supplierCode, row.supplierName)}
                            </span>
                          </div>
                          <span style={{
                            fontSize: "10px", padding: "2px 8px", borderRadius: "4px",
                            background: isOnline ? "#f0fdf4" : "#f1f5f9",
                            color: isOnline ? "#059669" : "#94a3b8", fontWeight: 600,
                          }}>
                            {isOnline ? "Online" : "Offline"}
                          </span>
                        </div>
                      );
                    })
              )}

              {modalCard === "totalBids" && (
                itemLeaderboard.length === 0
                  ? <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>No bid data available.</div>
                  : itemLeaderboard.map((item, idx) => (
                      <div key={idx} style={{
                        padding: "10px 14px", borderBottom: "1px solid #f1f5f9",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        background: idx % 2 === 0 ? "#fff" : "#fafafa",
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: "12px", color: "#1e293b" }}>
                            {item.itemCode || `Item ${idx + 1}`}
                          </div>
                          {item.itemDesc && (
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{item.itemDesc}</div>
                          )}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: "13px", color: "#d97706" }}>
                          {item.rankings?.length || 0} Bids
                        </div>
                      </div>
                    ))
              )}

              {modalCard === "extensions" && (
                <div style={{ padding: "16px", textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: "28px", color: "#db2777", marginBottom: "8px" }}>
                    {stats.extensions}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Auto Extension: {stats.autoExtensionMins} minutes
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                    Auction extends automatically when a bid is placed near closing time.
                  </div>
                </div>
              )}

              {modalCard === "endTime" && (
                <div style={{ padding: "16px", textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: "16px", color: "#0f766e", marginBottom: "4px" }}>
                    {stats.formattedEnd}
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>(IST)</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
};

// ── Mini Sparkline (decorative) 
const MiniSparkline = () => (
  <svg width="50" height="20" viewBox="0 0 50 20" fill="none" style={{ display: "block" }}>
    <polyline
      points="0,15 8,10 16,14 24,8 32,12 40,6 50,10"
      stroke="#0f766e"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Styles 
const statCardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  minHeight: "130px",
};

const statIconWrap = {
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "8px",
};

const statLabelStyle = {
  fontSize: "10px",
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "4px",
};

const statValueStyle = {
  fontSize: "18px",
  fontWeight: 800,
};
// eslint-disable-next-line
const statSubStyle = {
  fontSize: "10px",
  color: "#94a3b8",
  marginTop: "2px",
};

const sectionCardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

const sectionHeaderStyle = {
  padding: "12px 16px",
  borderBottom: "1px solid #e2e8f0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fafbfc",
};

const thStyle = {
  padding: "8px 12px",
  textAlign: "left",
  color: "#64748b",
  fontWeight: 700,
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const tdStyle = {
  padding: "8px 12px",
  verticalAlign: "middle",
};
// eslint-disable-next-line
const viewAllBtnStyle = {
  background: "none",
  border: "none",
  color: "#0f766e",
  fontWeight: 700,
  fontSize: "12px",
  cursor: "pointer",
  padding: "4px 8px",
};

export default LiveAuctionDashboard;
